import React, { Fragment, useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
  Label,
  Input,
  FormGroup,
} from "reactstrap";
import { Breadcrumbs } from "../../AbstractElements";
import { useTranslation } from "react-i18next";
import { sentenceCase, titleCase } from "../../utils/case";

// API
import { deleteReceiptLogo, getSettings, updateSetting, uploadImage } from "../../api/setting.api";

// Draft WYSIWYG
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import {
  EditorState,
  convertToRaw,
  ContentState,
} from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { toast } from "react-toastify";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";

const SettingsMain = () => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState("0");
  const [loading, setLoading] = useState(false);

  const [receiptFormat, setReceiptFormat] = useState("");
  const [isDirty, setIsDirty] = useState(false);

  const [receiptLogo, setReceiptLogo] = useState(null);
  const [receiptLogoPreview, setReceiptLogoPreview] = useState(null);

  const [receiptTitle, setReceiptTitle] = useState("");
  const [issuerName, setIssuerName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [footerNote, setFooterNote] = useState("");
  const [readyTab, setReadyTab] = useState(null);
  const [originalData, setOriginalData] = useState(null);

  // Draft editors
  const LANGS = ["en", "cn", "bm"];

  // PRIVACY
  const [privacyEditors, setPrivacyEditors] = useState({
    en: EditorState.createEmpty(),
    cn: EditorState.createEmpty(),
    bm: EditorState.createEmpty(),
  });

  // REFUND
  const [refundEditors, setRefundEditors] = useState({
    en: EditorState.createEmpty(),
    cn: EditorState.createEmpty(),
    bm: EditorState.createEmpty(),
  });

  // TERMS
  const [tcSummaryEditors, setTcSummaryEditors] = useState({
    en: EditorState.createEmpty(),
    cn: EditorState.createEmpty(),
    bm: EditorState.createEmpty(),
  });

  const [tcFullEditors, setTcFullEditors] = useState({
    en: EditorState.createEmpty(),
    cn: EditorState.createEmpty(),
    bm: EditorState.createEmpty(),
  });

  const [siteName, setSiteName] = useState("");
  const [minLeadTime, setMinLeadTime] = useState({ value: "", type: "" });
  const [maxLeadTime, setMaxLeadTime] = useState({ value: "", type: "" });
  const [monthlyRefreshDay, setMonthlyRefreshDay] = useState("");
  const [paymentDetailsEditor, setPaymentDetailsEditor] = useState(() =>
    EditorState.createEmpty()
  );
  const [peakDates, setPeakDates] = useState([]);
  const [holidayDates, setHolidayDates] = useState([]);
  const [mode, setMode] = useState("peak");
  const [regularPeak, setRegularPeak] = useState({
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  });
  const [paymentMethods, setPaymentMethods] = useState({
    enabled: [],
    content: "",
  });
  const [beingDonationOf, setBeingDonationOf] = useState("");

  const PAGE_URLS = {
    general: "https://booking.chinfumc.org.my",
    privacy: "https://booking.chinfumc.org.my/privacypolicy",
    refund: "https://booking.chinfumc.org.my/refundpolicy",
    terms: "https://booking.chinfumc.org.my/termsandconditions",
  };

  const convertEditorToHtml = (editor) =>
    draftToHtml(convertToRaw(editor.getCurrentContent()));

  // =============================
  // INIT LOAD
  // =============================
  useEffect(() => {
    const init = async () => {
      try {
        const data = await getSettings();
        setOriginalData(data);

        const paymentMethod = data.find((x) => x.item === "payment method");
        const monthlyRefresh = data.find((x) => x.item === "monthly refresh date");
        const defaultLead = data.find((x) => x.item === "default lead time");
        const pageTitle = data.find((x) => x.item === "page title");
        const receipt = data.find((x) => x.item === "receipt");
        const privacy = data.find((x) => x.item === "privacy policy");
        const refund = data.find((x) => x.item === "refund policy");
        const terms = data.find((x) => x.item === "terms & conditions");
        const peak = data.find((x) => x.item === "peak season");
        const holiday = data.find((x) => x.item === "holiday");

        // ================= GENERAL =================
        // PAGE TITLE
        if (pageTitle?.details) {
          setSiteName(pageTitle.details.title || "");
        }

        // DEFAULT LEAD TIME
        if (defaultLead?.details) {
          setMinLeadTime(defaultLead.details.min || { value: "", type: "" });
          setMaxLeadTime(defaultLead.details.max || { value: "", type: "" });
        }

        // MONTHLY REFRESH DATE
        if (monthlyRefresh?.details) {
          setMonthlyRefreshDay(monthlyRefresh.details.day || "");
        }

        // PAYMENT METHOD DETAILS
        if (paymentMethod?.details) {
          const { enabled, content } = paymentMethod.details;

          setPaymentMethods({
            enabled: enabled || [],
            content: content || "",
          });

          if (content) {
            const blocks = htmlToDraft(content);
            const parsed = ContentState.createFromBlockArray(blocks.contentBlocks);
            setPaymentDetailsEditor(EditorState.createWithContent(parsed));
          }
        }

        // ================= RECEIPT =================
        if (receipt?.details) {
          const d = receipt.details;

          setReceiptFormat(d.receiptNumberFormat || "");
          setReceiptTitle(d.receiptTitle || "");
          setIssuerName(d.issuerName || "");
          setFooterNote(d.footerNote || "");
          setReceiptLogoPreview(d.logo || null);
          setReceiptLogoPreview(
            d.logo ? `https://bfiles.chinfumc.org.my/${d.logo}` : null
          );
          setBeingDonationOf(d.beingDonationOf || "");
        }

        // helper
        const buildEditor = (html) => {
          if (!html) return EditorState.createEmpty();
          const blocks = htmlToDraft(html);
          const content = ContentState.createFromBlockArray(
            blocks.contentBlocks
          );
          return EditorState.createWithContent(content);
        };

        // ================= PRIVACY =================
        if (privacy?.details?.content) {
          const content = privacy.details.content;

          setPrivacyEditors({
            en: buildEditor(content.en),
            cn: buildEditor(content.cn),
            bm: buildEditor(content.bm),
          });
        }

        // ================= REFUND =================
        if (refund?.details?.content) {
          const content = refund.details.content;

          setRefundEditors({
            en: buildEditor(content.en),
            cn: buildEditor(content.cn),
            bm: buildEditor(content.bm),
          });
        }

        // ================= TERMS =================
        if (terms?.details) {
          setTcSummaryEditors({
            en: buildEditor(terms.details.summary?.en),
            cn: buildEditor(terms.details.summary?.cn),
            bm: buildEditor(terms.details.summary?.bm),
          });

          setTcFullEditors({
            en: buildEditor(terms.details.full?.en),
            cn: buildEditor(terms.details.full?.cn),
            bm: buildEditor(terms.details.full?.bm),
          });
        }

        if (peak?.details) {
          setRegularPeak(peak?.details?.regular || {
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: [],
            sunday: [],
          });
          setPeakDates(flattenDates(peak?.details?.dates));
        }

        if (holiday?.details) {
          setHolidayDates(flattenDates(holiday?.details));
        }

        setIsDirty(false);
      } catch (err) {
        console.error(err);
      }
    };

    init();
  }, []);

  const updateLangEditor = (setter, lang, state) => {
    setter((prev) => ({
      ...prev,
      [lang]: state,
    }));
    setIsDirty(true);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReceiptLogo(file);
      setReceiptLogoPreview(URL.createObjectURL(file));
      setIsDirty(true);
    }
  };

  const insertToken = (token) => {
    setReceiptFormat((prev) => `${prev}${token}`);
    setIsDirty(true);
  };

  const resetReceiptFormat = () => {
    setReceiptFormat("");
    setIsDirty(true);
  };

  const resetAllFields = () => {
    if (!originalData) return;

    const paymentMethod = originalData.find((x) => x.item === "payment method");
    const monthlyRefresh = originalData.find((x) => x.item === "monthly refresh date");
    const defaultLead = originalData.find((x) => x.item === "default lead time");
    const pageTitle = originalData.find((x) => x.item === "page title");
    const receipt = originalData.find((x) => x.item === "receipt");
    const privacy = originalData.find((x) => x.item === "privacy policy");
    const refund = originalData.find((x) => x.item === "refund policy");
    const terms = originalData.find((x) => x.item === "terms & conditions");
    const peak = originalData.find((x) => x.item === "peak season");
    const holiday = originalData.find((x) => x.item === "holiday");

    // helper
    const buildEditor = (html) => {
      if (!html) return EditorState.createEmpty();

      const blocks = htmlToDraft(html);
      const content = ContentState.createFromBlockArray(
        blocks.contentBlocks
      );

      return EditorState.createWithContent(content);
    };

    // ================= GENERAL =================
    // PAGE TITLE
    if (pageTitle?.details) {
      setSiteName(pageTitle.details.title || "");
    }

    // DEFAULT LEAD TIME
    if (defaultLead?.details) {
      setMinLeadTime(defaultLead.details.min || { value: "", type: "" });
      setMaxLeadTime(defaultLead.details.max || { value: "", type: "" });
    }

    // MONTHLY REFRESH DATE
    if (monthlyRefresh?.details) {
      setMonthlyRefreshDay(monthlyRefresh.details.day || "");
    }

    // PAYMENT METHOD
    if (paymentMethod?.details) {
      const { enabled, content } = paymentMethod.details;

      setPaymentMethods({
        enabled: enabled || [],
        content: content || "",
      });

      if (content) {
        const blocks = htmlToDraft(content);
        const parsed = ContentState.createFromBlockArray(blocks.contentBlocks);
        setPaymentDetailsEditor(EditorState.createWithContent(parsed));
      } else {
        setPaymentDetailsEditor(EditorState.createEmpty());
      }
    }
    
    // ================= RECEIPT =================
    if (receipt?.details) {
      const d = receipt.details;

      setReceiptFormat(d.receiptNumberFormat || "");
      setReceiptTitle(d.receiptTitle || "");
      setIssuerName(d.issuerName || "");
      setFooterNote(d.footerNote || "");
      setReceiptLogoPreview(d.logo || null);
      setReceiptLogo(null);
      setBeingDonationOf(d.beingDonationOf || "");
    }

    // ================= PRIVACY =================
    if (privacy?.details?.content) {
      setPrivacyEditors({
        en: buildEditor(privacy?.details?.content?.en),
        cn: buildEditor(privacy?.details?.content?.cn),
        bm: buildEditor(privacy?.details?.content?.bm),
      });
    }


    // ================= REFUND =================
    if (refund?.details?.content) {
      setRefundEditors({
        en: buildEditor(refund?.details?.content?.en),
        cn: buildEditor(refund?.details?.content?.cn),
        bm: buildEditor(refund?.details?.content?.bm),
      });
    }

    // ================= TERMS =================
    if (terms?.details) {
      if (terms.details.summary) {
        setTcSummaryEditors({
          en: buildEditor(terms?.details?.summary?.content?.en),
          cn: buildEditor(terms?.details?.summary?.content?.cn),
          bm: buildEditor(terms?.details?.summary?.content?.bm),
        });
      }

      if (terms.details.full) {
        setTcFullEditors({
          en: buildEditor(terms?.details?.full?.content?.en),
          cn: buildEditor(terms?.details?.full?.content?.cn),
          bm: buildEditor(terms?.details?.full?.content?.bm),
        });
      }
    }

    if (peak?.details) {
      setPeakDates(flattenDates(peak?.details));
    }

    if (holiday?.details) {
      setHolidayDates(flattenDates(holiday?.details));
    }

    setIsDirty(false);
  };

  const handleTabChange = (nextTab) => {
    if (isDirty) {
      const confirmed = window.confirm(
        "You have unsaved changes. Do you want to switch tabs?"
      );
      if (!confirmed) return
      else resetAllFields();
    }

    setActiveTab(nextTab);
    setReadyTab(null);

    // delay mount
    setTimeout(() => {
      setReadyTab(nextTab);
    }, 0);
  };

  const handleViewPage = (type) => {
    const url = PAGE_URLS[type];
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const handleDateClick = (info) => {
    const date = info.dateStr;

    if (mode === "peak") {
      setPeakDates((prev) =>
        prev.includes(date)
          ? prev.filter((d) => d !== date)
          : [...prev, date]
      );
    }

    if (mode === "holiday") {
      setHolidayDates((prev) =>
        prev.includes(date)
          ? prev.filter((d) => d !== date)
          : [...prev, date]
      );
    }

    setIsDirty(true);
  };

  const handleEventClick = (info) => {
    const date = info.event.startStr; // "YYYY-MM-DD"
    const type = info.event.title; // "peak" or "holiday"

    if (type === "peak") {
      setPeakDates((prev) => prev.filter((d) => d !== date));
    }

    if (type === "holiday") {
      setHolidayDates((prev) => prev.filter((d) => d !== date));
    }

    setIsDirty(true);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // ================= GENERAL =================
      if (activeTab === "0") {
        await Promise.all([
          // PAGE TITLE
          updateSetting({
            item: "page title",
            details: {
              title: siteName,
            },
          }),

          // DEFAULT LEAD TIME
          updateSetting({
            item: "default lead time",
            details: {
              min: {
                value: minLeadTime.value,
                type: minLeadTime.type,
              },
              max: {
                value: maxLeadTime.value,
                type: maxLeadTime.type,
              },
            },
          }),

          // MONTHLY REFRESH DATE
          updateSetting({
            item: "monthly refresh date",
            details: {
              day: monthlyRefreshDay,
            },
          }),
        ]);
      }

      // ================= RECEIPT =================
      if (activeTab === "1") {
        if (receiptLogo) {
          const formData = new FormData();
          formData.append("item", "receipt");

          formData.append(
            "details",
            JSON.stringify({
              receiptNumberFormat: receiptFormat,
              receiptTitle,
              issuerName,
              address,
              phone,
              email,
              footerNote,
              beingDonationOf,
            })
          );

          formData.append("file", receiptLogo);

          await updateSetting(formData);
        } else {
          await updateSetting({
            item: "receipt",
            details: {
              receiptNumberFormat: receiptFormat,
              receiptTitle,
              issuerName,
              address,
              phone,
              email,
              footerNote,
              beingDonationOf,
            },
          });
        }
      }

      // ================= PRIVACY =================
      if (activeTab === "2") {
        await updateSetting({
          item: "privacy policy",
          details: {
            content: {
              en: convertEditorToHtml(privacyEditors.en),
              cn: convertEditorToHtml(privacyEditors.cn),
              bm: convertEditorToHtml(privacyEditors.bm),
            },
          },
        });
      }

      // ================= REFUND =================
      if (activeTab === "3") {
        await updateSetting({
          item: "refund policy",
          details: {
            content: {
              en: convertEditorToHtml(refundEditors.en),
              cn: convertEditorToHtml(refundEditors.cn),
              bm: convertEditorToHtml(refundEditors.bm),
            },
          },
        });
      }

      // ================= TERMS =================
      if (activeTab === "4") {
        await updateSetting({
          item: "terms & conditions",
          details: {
            summary: {
              en: convertEditorToHtml(tcSummaryEditors.en),
              cn: convertEditorToHtml(tcSummaryEditors.cn),
              bm: convertEditorToHtml(tcSummaryEditors.bm),
            },
            full: {
              en: convertEditorToHtml(tcFullEditors.en),
              cn: convertEditorToHtml(tcFullEditors.cn),
              bm: convertEditorToHtml(tcFullEditors.bm),
            },
          },
        });
      }

      // ================= SEASONS & HOLIDAYS =================
      if (activeTab === "5") {
        const peakGrouped = groupByYear([...new Set(peakDates)]);
        const holidayGrouped = groupByYear([...new Set(holidayDates)]);

        await updateSetting({
          item: "peak season",
          details: {
            regular: regularPeak,
            dates: peakGrouped
          },
        });

        await updateSetting({
          item: "holiday",
          details: holidayGrouped,
        });
      }

      if (activeTab === "6") {
        // PAYMENT METHOD / DETAILS
        await updateSetting({
          item: "payment method",
          details: {
            enabled: paymentMethods.enabled,
            content: draftToHtml(
              convertToRaw(paymentDetailsEditor.getCurrentContent())
            ),
          },
        })
      }

      toast.success("Settings saved successfully");
      setIsDirty(false);
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const groupByYear = (dates) => {
    return dates.reduce((acc, date) => {
      const year = date.split("-")[0];
      if (!acc[year]) acc[year] = [];
      acc[year].push(date);
      return acc;
    }, {});
  };

  const flattenDates = (obj) => {
    if (!obj) return [];
    return Object.values(obj).flat();
  };

  const calendarEvents = [
    ...peakDates.map((date) => ({
      title: "peak",
      date,
      color: "#dc3545",
    })),
    ...holidayDates.map((date) => ({
      title: "holiday",
      date,
      color: "#0d6efd",
    })),
  ];

  const uploadImageCallback = (file) => {
    return new Promise(async (resolve, reject) => {
      try {
        // optional validation
        if (!file.type.startsWith("image/")) {
          return reject("Only image files allowed");
        }

        const res = await uploadImage(file, "editor");

        resolve({
          data: {
            link: res.url, // DraftJS required format
          },
        });
      } catch (err) {
        console.error("Image upload failed:", err);
        reject(err);
      }
    });
  };

  const handleDeleteReceiptLogo = async () => {
    try {
      if (!window.confirm("Are you sure you want to delete this logo?")) return;

      await deleteReceiptLogo();

      // Clear local state
      setReceiptLogo(null);
      setIsDirty(true);

      alert("Logo deleted successfully");
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to delete logo");
    }
  };

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("settings"))}
        title={titleCase(t("settings"))}
        mainTitle={titleCase(t("settings"))}
      />

      <Container fluid>
        <Row className="project-card">
          <Col className="project-list">
            <Card>
              <Row>
                <Col>
                  <Nav tabs className="border-tab">
                    <NavItem>
                      <NavLink
                        className={activeTab === "0" ? "active" : ""}
                        onClick={() => handleTabChange("0")}
                      >
                        {titleCase(t("general"))}
                      </NavLink>
                    </NavItem>

                    <NavItem>
                      <NavLink
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => handleTabChange("1")}
                      >
                        {titleCase(t("receipt"))}
                      </NavLink>
                    </NavItem>

                    <NavItem>
                      <NavLink
                        className={activeTab === "2" ? "active" : ""}
                        onClick={() => handleTabChange("2")}
                      >
                        {titleCase(t("privacy policy"))}
                      </NavLink>
                    </NavItem>

                    <NavItem>
                      <NavLink
                        className={activeTab === "3" ? "active" : ""}
                        onClick={() => handleTabChange("3")}
                      >
                        {titleCase(t("refund policy"))}
                      </NavLink>
                    </NavItem>

                    <NavItem>
                      <NavLink
                        className={activeTab === "4" ? "active" : ""}
                        onClick={() => handleTabChange("4")}
                      >
                        {titleCase(t("terms & conditions"))}
                      </NavLink>
                    </NavItem>

                    <NavItem>
                      <NavLink
                        className={activeTab === "5" ? "active" : ""}
                        onClick={() => handleTabChange("5")}
                      >
                        {titleCase(t("seasons & holidays"))}
                      </NavLink>
                    </NavItem>

                    <NavItem>
                      <NavLink
                        className={activeTab === "6" ? "active" : ""}
                        onClick={() => handleTabChange("6")}
                      >
                        {titleCase(t("payment methods"))}
                      </NavLink>
                    </NavItem>
                  </Nav>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col>
            <Card>
              <CardBody>
                <TabContent activeTab={activeTab}>
                  {/* ================= GENERAL ================= */}
                  <TabPane tabId="0">
                    <Row>
                      <Col>
                        <div className="d-flex justify-content-end mb-2">
                          <Button
                            color="outline-info"
                            size="sm"
                            onClick={() => handleViewPage("general")}
                          >
                            {titleCase(t("view page"))}
                          </Button>
                        </div>
                      </Col>
                    </Row>
                    {/* ================= SITE NAME ================= */}
                    <Row>
                      <Col md="12">
                        <Label>{titleCase(t("site name"))}</Label>
                        <Input
                          value={siteName}
                          onChange={(e) => {
                            setSiteName(e.target.value);
                            setIsDirty(true);
                          }}
                        />
                      </Col>
                    </Row>

                    <hr className="my-4" />

                    {/* ================= LEAD TIMES + REFRESH DAY ================= */}
                    <Row>
                      {/* MIN LEAD TIME */}
                      <Col md="6">
                        <Label>{titleCase(t("min lead time"))}</Label>
                        <Row>
                          <Col md="6">
                            <Input
                              type="number"
                              min="0"
                              placeholder={titleCase(t("value"))}
                              value={minLeadTime?.value || ""}
                              onChange={(e) => {
                                setMinLeadTime((prev) => ({
                                  ...prev,
                                  value: e.target.value,
                                }));
                                setIsDirty(true);
                              }}
                            />
                          </Col>
                          <Col md="6">
                            <Input
                              type="select"
                              value={minLeadTime?.type || ""}
                              onChange={(e) => {
                                setMinLeadTime((prev) => ({
                                  ...prev,
                                  type: e.target.value,
                                }));
                                setIsDirty(true);
                              }}
                            >
                              <option value="">{titleCase(t("type"))}</option>
                              <option value="day">{titleCase(t("day"))}</option>
                              <option value="month">{titleCase(t("month"))}</option>
                              <option value="year">{titleCase(t("year"))}</option>
                            </Input>
                          </Col>
                        </Row>
                        <small className="text-muted">
                          {t("min lead time explanation")}
                        </small>
                      </Col>

                      {/* MAX LEAD TIME */}
                      <Col md="6">
                        <Label>{titleCase(t("max lead time"))}</Label>
                        <Row>
                          <Col md="6">
                            <Input
                              type="number"
                              min="0"
                              placeholder={titleCase(t("value"))}
                              value={maxLeadTime?.value || ""}
                              onChange={(e) => {
                                setMaxLeadTime((prev) => ({
                                  ...prev,
                                  value: e.target.value,
                                }));
                                setIsDirty(true);
                              }}
                            />
                          </Col>
                          <Col md="6">
                            <Input
                              type="select"
                              value={maxLeadTime?.type || ""}
                              onChange={(e) => {
                                setMaxLeadTime((prev) => ({
                                  ...prev,
                                  type: e.target.value,
                                }));
                                setIsDirty(true);
                              }}
                            >
                              <option value="">{titleCase(t("type"))}</option>
                              <option value="day">{titleCase(t("day"))}</option>
                              <option value="month">{titleCase(t("month"))}</option>
                              <option value="year">{titleCase(t("year"))}</option>
                              <option value="month_end">{titleCase(t("next n month end"))}</option>
                              <option value="year_end">{titleCase(t("next n year end"))}</option>
                            </Input>
                          </Col>
                        </Row>
                        <small className="text-muted">
                          {t("max lead time explanation")}
                        </small>
                      </Col>

                      {/* MONTHLY REFRESH DATE */}
                      <Col md="12" className="mt-4">
                        <Label>{titleCase(t("monthly refresh date"))}</Label>

                        <Input
                          type="number"
                          min="1"
                          max="31"
                          placeholder={titleCase(t("day of month (1-31)"))}
                          value={monthlyRefreshDay || ""}
                          onChange={(e) => {
                            setMonthlyRefreshDay(e.target.value);
                            setIsDirty(true);
                          }}
                        />

                        <small className="text-muted">
                          {sentenceCase(t("monthly refresh explanation"))}
                        </small>
                      </Col>
                    </Row>
                  </TabPane>

                  {/* ================= RECEIPT ================= */}
                  <TabPane tabId="1">
                    <CardBody>
                      <Row>
                        <Col md="3">
                          <div
                            style={{
                              border: "1px dashed #ccc",
                              height: "120px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                            }}
                          >
                            {receiptLogoPreview ? (
                              <img
                                src={receiptLogoPreview}
                                alt="logo preview"
                                style={{ maxWidth: "100%", maxHeight: "100%" }}
                              />
                            ) : (
                              <span>{titleCase(t("no logo"))}</span>
                            )}
                          </div>
                        </Col>

                        <Col md="9">
                          <Label>{titleCase(t("logo"))}</Label>
                          <Input type="file" accept="image/*" onChange={handleLogoChange} />

                          {receiptLogoPreview && (
                            <div className="mt-2">
                              <Button
                                color="danger"
                                size="sm"
                                onClick={handleDeleteReceiptLogo}
                              >
                                {titleCase(t("delete logo"))}
                              </Button>
                            </div>
                          )}
                        </Col>
                      </Row>

                      <Row className="mt-5">
                        <Col md="12">
                          <Label>{titleCase(t("receipt number format"))}</Label>

                          <div className="d-flex gap-2 flex-wrap mb-2">
                            <Button size="sm" color="outline-info" onClick={() => insertToken("{RUNNING}")}>{titleCase(t("running number"))}</Button>
                            <Button size="sm" color="outline-info" onClick={() => insertToken("{YY}")}>{titleCase(t("year"))} - YY</Button>
                            <Button size="sm" color="outline-info" onClick={() => insertToken("{YYYY}")}>{titleCase(t("year"))} - YYYY</Button>
                            <Button size="sm" color="outline-info" onClick={() => insertToken("{M}")}>{titleCase(t("month"))} - M</Button>
                            <Button size="sm" color="outline-info" onClick={() => insertToken("{MM}")}>{titleCase(t("month"))} - MM</Button>
                            <Button size="sm" color="outline-info" onClick={() => insertToken("{D}")}>{titleCase(t("date"))} - D</Button>
                            <Button size="sm" color="outline-info" onClick={() => insertToken("{DD}")}>{titleCase(t("date"))} - DD</Button>
                          </div>

                          <Input
                            type="text"
                            value={receiptFormat}
                            onChange={(e) => {
                              setReceiptFormat(e.target.value);
                              setIsDirty(true);
                            }}
                          />
                        </Col>
                      </Row>

                      <Row className="mt-5">
                        <Col md="6">
                          <Label>{titleCase(t("receipt title"))}</Label>
                          <Input value={receiptTitle} onChange={(e)=>{setReceiptTitle(e.target.value);setIsDirty(true);}} />
                        </Col>

                        <Col md="6">
                          <Label>{titleCase(t("issuer name"))}</Label>
                          <Input value={issuerName} onChange={(e)=>{setIssuerName(e.target.value);setIsDirty(true);}} />
                        </Col>

                        <Col md="6" className="mt-3">
                          <Label>{titleCase(t("address"))}</Label>
                          <Input value={address} onChange={(e)=>{setAddress(e.target.value);setIsDirty(true);}} />
                        </Col>

                        <Col md="6" className="mt-3">
                          <Label>{titleCase(t("phone number"))}</Label>
                          <Input value={phone} onChange={(e)=>{setPhone(e.target.value);setIsDirty(true);}} />
                        </Col>

                        <Col md="6" className="mt-3">
                          <Label>{titleCase(t("email"))}</Label>
                          <Input value={email} onChange={(e)=>{setEmail(e.target.value);setIsDirty(true);}} />
                        </Col>

                        <Col md="6" className="mt-3">
                          <Label>{titleCase(t("being donation of"))}</Label>
                          <Input
                            value={beingDonationOf}
                            onChange={(e) => {
                              setBeingDonationOf(e.target.value);
                              setIsDirty(true);
                            }}
                          />
                        </Col>

                        <Col md="12" className="mt-3">
                          <Label>{titleCase(t("footer note"))}</Label>
                          <textarea
                            className="form-control"
                            rows="3"
                            value={footerNote}
                            onChange={(e)=>{setFooterNote(e.target.value);setIsDirty(true);}}
                          />
                        </Col>
                      </Row>
                    </CardBody>
                  </TabPane>

                  {/* ================= PRIVACY POLICY ================= */}
                  <TabPane tabId="2">
                    <div className="d-flex justify-content-end mb-2">
                      <Button
                        color="outline-info"
                        size="sm"
                        onClick={() => handleViewPage("privacy")}
                      >
                        {titleCase(t("view page"))}
                      </Button>
                    </div>
                    {LANGS.map((lang) => (
                      <div key={lang} className="mb-4">
                        <Label>{titleCase(t(`${lang}`))}</Label>

                        <Editor
                          editorState={privacyEditors[lang]}
                          onEditorStateChange={(state) =>
                            updateLangEditor(setPrivacyEditors, lang, state)
                          }
                          wrapperStyle={{ border: "solid 0.5px #d9d9d9", minHeight: "30vh" }}
                          editorStyle={{ padding: "0 15px", lineHeight: "1.5" }}
                        />
                      </div>
                    ))}
                  </TabPane>

                  {/* ================= REFUND POLICY ================= */}
                  <TabPane tabId="3">
                    <div className="d-flex justify-content-end mb-2">
                      <Button
                        color="outline-info"
                        size="sm"
                        onClick={() => handleViewPage("refund")}
                      >
                        {titleCase(t("view page"))}
                      </Button>
                    </div>
                    {LANGS.map((lang) => (
                      <div key={lang} className="mb-4">
                        <Label>{titleCase(t(`${lang}`))}</Label>

                        <Editor
                          editorState={refundEditors[lang]}
                          onEditorStateChange={(state) =>
                            updateLangEditor(setRefundEditors, lang, state)
                          }
                          wrapperStyle={{ border: "solid 0.5px #d9d9d9", minHeight: "30vh" }}
                          editorStyle={{ padding: "0 15px" }}
                        />
                      </div>
                    ))}
                  </TabPane>

                  {/* ================= TERMS ================= */}
                  <TabPane tabId="4">
                    {/* <Row>
                      <Col>
                        <Label>{titleCase(t("summarized terms"))}</Label>
                        <Button
                          color="outline-info m-l-10"
                          size="sm"
                          onClick={() => handleViewPage("general")}
                        >
                          {titleCase(t("view page"))}
                        </Button>
                        {LANGS.map((lang) => (
                          <div key={lang} className="mb-4">
                            <Label>{titleCase(t(`${lang}`))}</Label>

                            <Editor
                              editorState={tcSummaryEditors[lang]}
                              onEditorStateChange={(state) =>
                                updateLangEditor(setTcSummaryEditors, lang, state)
                              }
                              wrapperStyle={{ border: "solid 0.5px #d9d9d9", minHeight: "30vh" }}
                              editorStyle={{ padding: "0 15px" }}
                            />
                          </div>
                        ))}
                      </Col>
                    </Row> */}

                    <Row className="mt-4">
                      <Col>
                        {/* <Label>{titleCase(t("full terms"))}</Label>
                        <Button
                          color="outline-info m-l-10"
                          size="sm"
                          onClick={() => handleViewPage("terms")}
                        >
                          {titleCase(t("view page"))}
                        </Button> */}
                        <div className="d-flex justify-content-end mb-2">
                          <Button
                            color="outline-info"
                            size="sm"
                            onClick={() => handleViewPage("terms")}
                          >
                            {titleCase(t("view page"))}
                          </Button>
                        </div>
                        {LANGS.map((lang) => (
                          <div key={lang} className="mb-4">
                            <Label>{titleCase(t(`${lang}`))}</Label>

                            <Editor
                              editorState={tcFullEditors[lang]}
                              onEditorStateChange={(state) =>
                                updateLangEditor(setTcFullEditors, lang, state)
                              }
                              wrapperStyle={{ border: "solid 0.5px #d9d9d9", minHeight: "30vh" }}
                              editorStyle={{ padding: "0 15px" }}
                            />
                          </div>
                        ))}
                      </Col>
                    </Row>
                  </TabPane>

                  {/* ================= SEASONS & HOLIDAYS ================= */}
                  <TabPane tabId="5">
                    <Row>
                      {/* LEFT SIDE - REGULAR PEAK */}
                      <Col md="4">
                        <Card>
                          <CardBody>
                            <h5>{titleCase(t("regular peak"))}</h5>

                            {Object.keys(regularPeak).map((day) => (
                              <div key={day} className="mb-4">
                                <Label className="fw-bold">{titleCase(t(day))}</Label>

                                {regularPeak[day].map((range, index) => (
                                  <Row key={index} className="align-items-center mb-2">
                                    <Col md="5">
                                      <Input
                                        type="time"
                                        value={range.start}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          setRegularPeak((prev) => {
                                            const updated = [...prev[day]];
                                            updated[index].start = value ? `${value}:00` : "";
                                            return { ...prev, [day]: updated };
                                          });
                                        }}
                                      />
                                    </Col>

                                    <Col md="5">
                                      <Input
                                        type="time"
                                        value={range.end}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          setRegularPeak((prev) => {
                                            const updated = [...prev[day]];
                                            updated[index].end = value ? `${value}:00` : "";
                                            return { ...prev, [day]: updated };
                                          });
                                        }}
                                      />
                                    </Col>

                                    <Col md="2">
                                      <Button
                                        color="outline-danger"
                                        size="sm"
                                        onClick={() => {
                                          setRegularPeak((prev) => {
                                            const updated = prev[day].filter((_, i) => i !== index);
                                            return { ...prev, [day]: updated };
                                          });
                                        }}
                                      >
                                        ×
                                      </Button>
                                    </Col>
                                  </Row>
                                ))}

                                <p>
                                  <Button
                                    size="sm"
                                    color="outline-primary"
                                    onClick={() => {
                                      setRegularPeak((prev) => ({
                                        ...prev,
                                        [day]: [...prev[day], { start: "", end: "" }],
                                      }));
                                    }}
                                  >
                                    {titleCase(t("add"))}
                                  </Button>
                                </p>
                              </div>
                            ))}
                          </CardBody>
                        </Card>
                      </Col>

                      {/* RIGHT SIDE - FULL CALENDAR */}
                      <Col md="8">
                        <Card>
                          <CardBody>
                            <h5>{titleCase(t("dates"))}</h5>

                            <div className="mb-3 d-flex gap-2">
                              <Button
                                color={mode === "peak" ? "danger" : "outline-danger"}
                                onClick={() => setMode("peak")}
                              >
                                {titleCase(t("peak season"))}
                              </Button>

                              <Button
                                color={mode === "holiday" ? "primary" : "outline-primary"}
                                onClick={() => setMode("holiday")}
                              >
                                {titleCase(t("holiday"))}
                              </Button>
                            </div>

                            <small className="text-muted d-block mb-3">
                              {mode === "peak"
                                ? t("click dates to mark peak")
                                : t("click dates to mark holiday")}
                            </small>

                            <FullCalendar
                              plugins={[dayGridPlugin, interactionPlugin]}
                              initialView="dayGridMonth"
                              events={calendarEvents}
                              dateClick={handleDateClick}
                              eventClick={handleEventClick}
                              height="auto"
                            />
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                  </TabPane>

                  <TabPane tabId="6">
                    {/* ================= PAYMENT METHODS ================= */}
                    <Row className="mb-3">
                      <Col md="12">
                        <Label>{titleCase(t("payment methods"))}</Label>

                        <FormGroup className="m-t-10 mb-0">
                          <div className="d-flex flex-wrap gap-3">

                            {/* Bank Transfer */}
                            <div className="checkbox checkbox-primary">
                              <Input
                                id="pm-bank-transfer"
                                type="checkbox"
                                checked={paymentMethods.enabled.includes("bank transfer")}
                                onChange={() => {
                                  const value = "bank transfer";

                                  setPaymentMethods((prev) => {
                                    const exists = prev.enabled.includes(value);

                                    const updatedEnabled = exists
                                      ? prev.enabled.filter((m) => m !== value)
                                      : [...prev.enabled, value];

                                    setIsDirty(true);

                                    return {
                                      ...prev,
                                      enabled: updatedEnabled,
                                    };
                                  });
                                }}
                              />
                              <Label for="pm-bank-transfer">
                                {titleCase(t("bank transfer"))}
                              </Label>
                            </div>

                            {/* S Pay Global */}
                            <div className="checkbox checkbox-primary">
                              <Input
                                disabled
                                id="pm-spay"
                                type="checkbox"
                                checked={paymentMethods.enabled.includes("s pay global")}
                                onChange={() => {
                                  const value = "s pay global";

                                  setPaymentMethods((prev) => {
                                    const exists = prev.enabled.includes(value);

                                    const updatedEnabled = exists
                                      ? prev.enabled.filter((m) => m !== value)
                                      : [...prev.enabled, value];

                                    setIsDirty(true);

                                    return {
                                      ...prev,
                                      enabled: updatedEnabled,
                                    };
                                  });
                                }}
                              />
                              <Label for="pm-spay">
                                {titleCase(t("s pay global"))}
                              </Label>
                            </div>

                            {/* FPX */}
                            <div className="checkbox checkbox-primary">
                              <Input
                                disabled
                                id="pm-fpx"
                                type="checkbox"
                                checked={paymentMethods.enabled.includes("fpx")}
                                onChange={() => {
                                  const value = "fpx";

                                  setPaymentMethods((prev) => {
                                    const exists = prev.enabled.includes(value);

                                    const updatedEnabled = exists
                                      ? prev.enabled.filter((m) => m !== value)
                                      : [...prev.enabled, value];

                                    setIsDirty(true);

                                    return {
                                      ...prev,
                                      enabled: updatedEnabled,
                                    };
                                  });
                                }}
                              />
                              <Label for="pm-fpx">{t("fpx").toUpperCase()}</Label>
                            </div>

                          </div>
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* ================= PAYMENT DETAILS ================= */}
                    <Row>
                      <Col md="12">
                        <Label>{titleCase(t("payment details"))}</Label>

                        <Editor
                          editorState={paymentDetailsEditor}
                          onEditorStateChange={(state) => {
                            setPaymentDetailsEditor(state);
                            setIsDirty(true);
                          }}
                          wrapperStyle={{
                            border: "solid 0.5px #d9d9d9",
                            minHeight: "30vh",
                          }}
                          editorStyle={{
                            paddingLeft: "15px",
                            paddingRight: "15px",
                          }}
                          toolbar={{
                            image: {
                              urlEnabled: true,
                              uploadEnabled: true,
                              alignmentEnabled: true,
                              uploadCallback: uploadImageCallback,
                              previewImage: true,
                              inputAccept: "image/jpeg,image/jpg,image/png",
                              alt: { present: false, mandatory: false },
                              defaultSize: {
                                height: "auto",
                                width: "400",
                              },
                            },
                          }}
                        />
                      </Col>
                    </Row>
                  </TabPane>
                </TabContent>

                {/* ================= ACTIONS ================= */}
                <div className="mt-4 text-end d-flex justify-content-end gap-2">
                  <Button color="secondary" onClick={resetReceiptFormat}>
                    {titleCase(t("reset"))}
                  </Button>

                  <Button color="primary" onClick={handleSubmit} disabled={loading}>
                    {loading ? "Saving..." : titleCase(t("save"))}
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default SettingsMain;